<template>
    <div class="rd">
        <div v-if="booking && booking.client" class="pagina">
            <header class="paginakop">
                <div class="tekst">
                    <h1>{{ booking.team.name }}</h1>
                    <p>{{ booking.experience.name }} · <Format :datetime="booking.date" /> · {{ booking.xcube.location.name }} · <span class="num">{{ booking.code }}</span></p>
                </div>
                <div class="rechts">
                    <button class="btn btn-stil btn-sm" type="button" @click="goBack">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
                        Back
                    </button>
                    <button v-if="canEditBooking" class="btn btn-paars btn-sm" type="button" @click="editBooking">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
                        Edit booking
                    </button>
                    <router-link class="btn btn-stil btn-sm" :to="{ name: 'bookings' }">All bookings</router-link>
                </div>
            </header>

            <div class="detail">
                <div class="paneel">
                    <div class="sectie">
                        <div class="paneel-kop" style="margin-bottom: 16px">
                            <h2>Game</h2>
                            <div class="rechts"><span class="badge" :class="statusBadge.cls">{{ statusBadge.label }}</span></div>
                        </div>
                        <dl class="dl">
                            <div><dt>Experience</dt><dd>{{ booking.experience.name }}</dd></div>
                            <div><dt>Players</dt><dd class="num">{{ booking.player_count }}</dd></div>
                            <div><dt>Location</dt><dd>{{ booking.xcube.location.name }}</dd></div>
                            <div><dt>X-Cube</dt><dd class="num">{{ booking.xcube.name }}</dd></div>
                            <div>
                                <dt>Local date and time</dt>
                                <dd>
                                    <Format :datetime="booking.date" />
                                    <button v-if="canMoveBooking" class="btn btn-stil btn-sm mini" type="button" @click="moveBooking">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 9l-3 3 3 3M2 12h13M19 15l3-3-3-3M22 12H9" /></svg>
                                        Move
                                    </button>
                                </dd>
                            </div>
                            <div><dt>X-Cube time</dt><dd>{{ xcubeTime }}</dd></div>
                            <div><dt>Language</dt><dd><span class="taal-tag">{{ (booking.language || "").toUpperCase() }}</span></dd></div>
                        </dl>
                    </div>

                    <div class="sectie">
                        <h3>Result</h3>
                        <p v-if="!played" class="leegwaarde" style="margin: 2px 0 0">
                            The game has not been played yet. Minutes played, score and ratings appear here afterwards.
                        </p>
                        <dl v-else class="dl">
                            <div><dt>Minutes played</dt><dd class="num">{{ booking.minutes_played != null ? booking.minutes_played + " min" : fallback }}</dd></div>
                            <div><dt>Score</dt><dd class="num">{{ booking.score != null ? booking.score : fallback }}</dd></div>
                            <div><dt>Game rating</dt><dd>{{ booking.rating ? booking.rating + " / 5" : fallback }}</dd></div>
                            <div><dt>Location rating</dt><dd>{{ booking.rating_location ? booking.rating_location + " / 5" : fallback }}</dd></div>
                        </dl>
                    </div>

                    <div class="sectie">
                        <h3>Customer</h3>
                        <dl class="dl">
                            <div>
                                <dt>Name</dt>
                                <dd>
                                    <template v-if="customerName">{{ customerName }}</template>
                                    <span v-else class="leegwaarde">Not filled in</span>
                                </dd>
                            </div>
                            <div><dt>Team name</dt><dd>{{ booking.team.name }}</dd></div>
                            <div>
                                <dt>Email</dt>
                                <dd>
                                    <template v-if="booking.client.email">{{ booking.client.email }}</template>
                                    <span v-else class="leegwaarde">Not filled in</span>
                                    <button v-if="booking.client.email && canResendBookingMail" class="btn btn-stil btn-sm mini" type="button" @click="handleResendBookingMail">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 6h16v12H4zM4 7l8 6 8-6" /></svg>
                                        Resend door code
                                    </button>
                                </dd>
                            </div>
                            <div><dt>Phone</dt><dd>{{ booking.client.phone_number || "" }}<span v-if="!booking.client.phone_number" class="leegwaarde">Not filled in</span></dd></div>
                            <div><dt>Consent to share photo</dt><dd>{{ played ? (booking.consent_to_share ? "Yes" : "No") : "" }}<span v-if="!played" class="leegwaarde">Decided after the game</span></dd></div>
                        </dl>
                    </div>

                    <div class="sectie">
                        <h3>Booking</h3>
                        <dl class="dl">
                            <div><dt>Door code</dt><dd><span class="deurcode">{{ booking.door_code }}</span></dd></div>
                            <div style="grid-column: 1 / -1">
                                <dt>Booking number</dt>
                                <dd>
                                    <div class="kopieer">
                                        <code ref="codeEl">{{ booking.code }}</code>
                                        <button class="btn btn-stil btn-icoon" type="button" aria-label="Copy booking number" @click="copyCode">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></svg>
                                        </button>
                                    </div>
                                </dd>
                            </div>
                            <div v-if="booking.deleted_at"><dt>Deleted</dt><dd>{{ xcubeDeletedTime }}</dd></div>
                        </dl>
                    </div>
                </div>

                <div style="display: flex; flex-direction: column; gap: 16px">
                    <div class="paneel">
                        <div class="paneel-kop"><h2>Team photo</h2></div>
                        <template v-if="booking.photos && booking.photos.length > 0">
                            <div class="foto">
                                <img :src="getPhotoUrl(booking.photos[0].url)" alt="Team photo" />
                            </div>
                            <div class="foto-links">
                                <a
                                    v-for="(photo, index) in booking.photos"
                                    :key="index"
                                    :href="getPhotoUrl(photo.url)"
                                    target="_blank"
                                    rel="noopener"
                                >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="m21 15-5-5L5 21" /></svg>
                                    Photo {{ index + 1 }}
                                </a>
                            </div>
                        </template>
                        <div v-else class="leeg" style="padding: 24px 8px">
                            <p>The photo appears here after the game. The X-Cube takes it at the end of the session.</p>
                        </div>
                    </div>

                    <div class="paneel">
                        <div class="paneel-kop"><h2>Booking type</h2></div>
                        <div v-if="lockRefundVoucher" class="melding-blok letop" role="note" style="margin-bottom: 14px">
                            <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"><path d="M12 7v6M12 17h.01" /></svg>
                            <div>This location uses an external booking system. If a customer wants a refund or a voucher, ask them to file it via <b>my.x-cube.nl</b>.</div>
                        </div>
                        <div class="schakel">
                            <span class="t"><span id="sw-refund">Refund</span><small>{{ hasSyncBookingSystem ? "Handled in the external booking system" : "Customer requested a refund" }}</small></span>
                            <button class="switch" type="button" role="switch" :aria-checked="booking.refund_requested" :disabled="lockRefundVoucher" aria-labelledby="sw-refund" @click="toggleRefund"></button>
                        </div>
                        <div class="schakel">
                            <span class="t"><span id="sw-voucher">Voucher</span><small>{{ hasSyncBookingSystem ? "Handled in the external booking system" : "Customer requested a voucher" }}</small></span>
                            <button class="switch" type="button" role="switch" :aria-checked="booking.voucher_requested" :disabled="lockRefundVoucher" aria-labelledby="sw-voucher" @click="toggleVoucher"></button>
                        </div>
                        <div class="schakel">
                            <span class="t"><span id="sw-demo">Demo</span><small>Not counted in revenue and statistics{{ isManagement ? "" : " · only X-Cube management can change this" }}</small></span>
                            <button class="switch" type="button" role="switch" :aria-checked="booking.demo" :disabled="!isManagement" aria-labelledby="sw-demo" @click="toggleDemoSwitch"></button>
                        </div>
                    </div>

                    <div v-if="showActions" class="paneel gevaar">
                        <div class="paneel-kop"><h2>Actions</h2></div>
                        <div class="knoprij">
                            <button v-if="canFinishBooking && !booking.finished" class="btn btn-ghost btn-sm" type="button" @click="handleFinishBooking">Mark as finished</button>
                            <button v-if="isManagement" class="btn btn-ghost btn-sm" type="button" @click="openInStrapi">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" /></svg>
                                Open in Strapi
                            </button>
                            <button v-if="canRecoverBooking" class="btn btn-ghost btn-sm" type="button" @click="recoverBooking">Restore booking</button>
                            <button v-else-if="canDeleteBooking" class="btn btn-ghost btn-sm" type="button" @click="confirmingDelete = true">Delete booking</button>
                        </div>
                        <div v-if="confirmingDelete" class="bevestig" style="margin-top: 12px">
                            <span>Delete the booking for <b>{{ booking.team.name }}</b>? The door code stops working.</span>
                            <div class="knoprij">
                                <button class="btn btn-rood btn-sm" type="button" @click="deleteBooking">Delete booking</button>
                                <button class="btn btn-stil btn-sm" type="button" @click="confirmingDelete = false">Keep it</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { Message } from "element-ui";
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router/composables";
import { fetchBookingData } from "../../api/graphql/bookings";
import { finishBooking, resendBookingMail, updateBooking } from "../../api/rest";
import { getTimeInTimeZone } from "../../helpers/dates";
import { getBookingStatus } from "../../helpers/bookingStatus";
import { toAbsoluteURL } from "../../helpers/download";
import {
    isManagement as determineIsManagement,
    isOperator as determineIsOperator,
    isOperatorAdmin as determineIsOperatorAdmin,
} from "../../helpers/auth";
import useCurrentUserStore from "../../store/userStore";
import Format from "./../shared/Format.vue";

const router = useRouter();
const route = useRoute();
const store = useCurrentUserStore();

const booking = ref(undefined);
const confirmingDelete = ref(false);
const codeEl = ref(null);
const fallback = "–";

const isManagement = computed(() => determineIsManagement(store.currentUser));
const isOperator = computed(() => determineIsOperator(store.currentUser));
const isOperatorAdmin = computed(() => determineIsOperatorAdmin(store.currentUser));
const isOperatorOrAdmin = computed(() => isOperator.value || isOperatorAdmin.value);

const played = computed(() => booking.value?.finished || booking.value?.minutes_played > 5);

const statusBadge = computed(() => getBookingStatus(booking.value));

// The person who booked (client first + last name). Shown on detail like the prototype;
// the list keeps team name + booker name.
const customerName = computed(() =>
    [booking.value?.client?.first_name, booking.value?.client?.last_name].filter(Boolean).join(" ").trim(),
);

const xcubeTime = computed(() =>
    booking.value?.xcube?.location ? getTimeInTimeZone(booking.value.date, booking.value.xcube.location.time_zone) : "",
);
const xcubeDeletedTime = computed(() =>
    booking.value?.xcube?.location ? getTimeInTimeZone(booking.value.deleted_at, booking.value.xcube.location.time_zone) : "",
);

const hasSyncBookingSystem = computed(() =>
    ["xpay", "ireserve"].includes(booking.value?.xcube?.location?.booking_system),
);
const lockRefundVoucher = computed(() => hasSyncBookingSystem.value && isOperator.value);

const getPhotoUrl = (url) => toAbsoluteURL(url);

// Return to the exact previous page (bookings filters/tab live in the URL, so history-back
// restores them). Fall back to the list when the detail was opened directly (no in-app
// history, e.g. a shared deep link) so Back never leaves the app.
const goBack = () => {
    if (window.history.length > 1) {
        router.back();
    } else {
        router.push({ name: "bookings" });
    }
};

const editBooking = () => router.push({ name: "edit-booking", params: { id: booking.value.id } });
const moveBooking = () => router.push({ name: "move-booking", params: { id: booking.value.id } });
const recoverBooking = () => router.push({ name: "create-booking", params: { booking: booking.value } });

const openInStrapi = () => {
    const strapiUrl = import.meta.env.VITE_APP_API_URL;
    if (!strapiUrl) {
        Message.error("Strapi URL is not configured.");
        return;
    }
    window.open(
        `${strapiUrl}/admin/plugins/content-manager/collectionType/application::booking.booking/${booking.value.id}`,
        "_blank",
    );
};

const copyCode = () => {
    const text = booking.value.code;
    const selectFallback = () => {
        const range = document.createRange();
        if (codeEl.value) {
            range.selectNodeContents(codeEl.value);
            const sel = getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
        }
        Message.info("Selected. Press Ctrl+C or ⌘C to copy.");
    };
    try {
        navigator.clipboard.writeText(text).then(() => Message.success("Booking number copied"), selectFallback);
    } catch {
        selectFallback();
    }
};

const toggleField = async (field, enabledMsg, disabledMsg) => {
    const next = !booking.value[field];
    booking.value[field] = next;
    try {
        const result = await updateBooking(booking.value.id, { [field]: next });
        booking.value[field] = result[field];
        Message.success(result[field] ? enabledMsg : disabledMsg);
    } catch (error) {
        booking.value[field] = !next;
        Message.error(`Error updating ${field}: ${error.message}`);
    }
};

const toggleRefund = () => toggleField("refund_requested", "Refund requested", "Refund request cancelled");
const toggleVoucher = () => toggleField("voucher_requested", "Voucher requested", "Voucher request cancelled");
const toggleDemoSwitch = () => toggleField("demo", "Demo enabled", "Demo disabled");

const handleFinishBooking = async () => {
    try {
        await finishBooking(booking.value.id);
        booking.value.finished = true;
        Message.success("Booking marked as finished");
    } catch (error) {
        Message.error(`Error finishing booking: ${error.message}`);
    }
};

const handleResendBookingMail = async () => {
    try {
        await resendBookingMail(booking.value.id, booking.value.client.email);
        Message.success(`Door code resent to ${booking.value.client.email}`);
    } catch (error) {
        Message.error(`Error resending booking mail: ${error.message}`);
    }
};

const deleteBooking = async () => {
    try {
        const result = await updateBooking(booking.value.id, { deleted_at: new Date().toISOString() });
        booking.value.deleted_at = result.deleted_at;
        confirmingDelete.value = false;
        Message.success("Booking deleted");
    } catch (error) {
        Message.error(`Error deleting booking: ${error.message}`);
    }
};

// Deleting a booking is an X-Cube management action only; operators and
// operator-admins never see the delete affordance (API stays authoritative).
const canDeleteBooking = computed(() => !booking.value.deleted_at && isManagement.value);
const canResendBookingMail = computed(() => !booking.value.finished);
const canFinishBooking = computed(() => !booking.value.finished && booking.value.end_of_game_mail_sent && isManagement.value);
const canMoveBooking = computed(() => !booking.value.minutes_played && !booking.value.deleted_at && (isManagement.value || isOperator.value));
const canRecoverBooking = computed(() => booking.value.deleted_at && (isManagement.value || isOperator.value));
// Management may edit any non-deleted booking (even a played/finished one);
// operators and operator-admins only before the game has been played.
const canEditBooking = computed(() => {
    if (booking.value.deleted_at) return false;
    if (isManagement.value) return true;
    return !played.value && isOperatorOrAdmin.value;
});
// Hide the whole Actions panel when the role has no action available here.
const showActions = computed(
    () => isManagement.value || canRecoverBooking.value || canDeleteBooking.value || canFinishBooking.value,
);

onMounted(async () => {
    booking.value = route.params.id;
    if (typeof booking.value !== "object") {
        booking.value = await fetchBookingData(booking.value);
    }
});
</script>
